import { useEffect } from 'react';
import type React from 'react';

import { Image, Text } from '@change/ds-core-ui';
import { CheckIcon } from '@change/ds-icons/CheckIcon';
import { EmailIcon } from '@change/ds-icons/EmailIcon';
import { GroupIcon } from '@change/ds-icons/GroupIcon';
import { SendIcon } from '@change/ds-icons/SendIcon';
import { cn } from '@change/ds-utils';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import megaphone from '#src/assets/megaphone-outreach.png';

import { useDecisionMakerOutreachStats } from '#src/app/shared/hooks/contactDecisionMaker';
import { focus } from '#src/app/shared/utils/dom';

import { ComposeSheet } from '../ComposeSheet';
import type { ContactDecisionMakerSuccessStageProps } from '../types';

import { useCountUp } from './hooks/useCountUp';

/**
 * Screen 5 (CHANGE-97533). The confirmation after a message is accepted for delivery.
 *
 * One more stage of the compose flow, so it wears the same `ComposeSheet` chrome as the story and
 * review stages — full height on a phone, the DS's centred dialog from `md` up — rather than taking
 * over the screen (CHANGE-99560; the DS deprecates full-screen modals on mobile for the same reason).
 * The DS modal keeps the page behind it inert and unscrollable, and the share sheet layers on top of
 * it rather than replacing it.
 */
export const Success: React.FC<ContactDecisionMakerSuccessStageProps> = ({
	decisionMaker,
	petitionId,
	flow,
	headingRef,
	isShareLoading,
}) => {
	const { translate, localizeNumber } = useI18n();
	const { contactedSupporterCount: reportedCount } = useDecisionMakerOutreachStats(petitionId);
	// The viewer just sent a message, so the total is at least one whatever the query says: the count
	// is served from a cache that can lag the viewer's own send, and en-US CLDR has no `zero` form, so
	// a 0 would select `other` and read "You're one of 0 supporters who've reached out".
	const contactedSupporterCount = reportedCount === undefined ? undefined : Math.max(1, reportedCount);
	const displayedCount = useCountUp(contactedSupporterCount);
	// Everyone but the viewer. The "joins N others" row only renders once there is at least one other
	// story to join — "joins 0 others" advertises the absence (Dan, 2026-09-23; CHANGE-99691).
	const otherSupporterCount = contactedSupporterCount === undefined ? undefined : contactedSupporterCount - 1;
	// The heading gets focus, not the dialog, so the first thing announced is what happened. This runs
	// before react-aria's own dialog focus (child effects first), which then leaves focus where it is.
	// Focus returns to the entry control when the flow closes — the flow owns that.
	useEffect(() => {
		focus(headingRef.current, { makeFocusable: true });
	}, [headingRef]);

	return (
		<ComposeSheet
			isOpen
			// Closing finishes the flow: the message is already on its way, so there is nothing to go back to.
			onOpenChange={(isOpen) => {
				if (!isOpen) flow.actions.finish();
			}}
			fullHeightOnMobile
			// The promise card has to be readable without scrolling on desktop (CHANGE-99691).
			fitContentOnDesktop
			// The heading sits in the body under the megaphone, as the design draws it; `heading` names the
			// dialog and the header keeps only the X.
			hideHeading
			heading={translate('corgi.contact_decision_maker.success.heading')}
			primaryAction={{
				label: translate('corgi.contact_decision_maker.success.share_cta'),
				onClick: flow.actions.openShare,
				// Waits for the share links rather than opening a sheet with nothing in it.
				loading: isShareLoading,
			}}
		>
			<div className="flex w-full flex-col items-center text-center" data-testid="contact-decision-maker-success">
				{/* The design's megaphone (`assets/megaphone-sm.png`, 400px for a 200px box). Decorative, so
				    `alt=""`; the heading carries the message. `aspect-auto` undoes the DS Image's 16:9 default,
				    which would stretch it sideways. */}
				<Image src={megaphone} alt="" className="aspect-auto h-[200px] w-auto" />

				{/* `tabIndex={-1}` keeps the heading focusable: it takes focus on open, and the share sheet
				    hands focus back to it on close. */}
				<Text as="h1" ref={headingRef} tabIndex={-1} className="text-size-heading-small text-typography-strong mt-6">
					<Translate value="corgi.contact_decision_maker.success.heading" />
				</Text>

				{/* Hidden until the count resolves rather than rendering a placeholder number: the sub-line's
				    whole job is to say how many people have done this, and a wrong figure undercuts it. */}
				{contactedSupporterCount !== undefined && (
					<Text className="text-size-body text-typography-weak mt-3 max-w-[300px]">
						{/* Intl output must not differ between SSR and hydration. */}
						<ClientRender>
							{/* `count` is the real total, not the animated value: pluralisation must not flip
							    mid-animation, which flashed the singular line for one tick on every total above one. */}
							<TranslatePlural
								value="corgi.contact_decision_maker.success.subline"
								count={contactedSupporterCount}
								replacements={{ supporterCount: localizeNumber(displayedCount ?? contactedSupporterCount) }}
							/>
						</ClientRender>
					</Text>
				)}

				<PromiseCard decisionMakerName={decisionMaker.displayName} otherSupporterCount={otherSupporterCount} />
			</div>
		</ComposeSheet>
	);
};

/**
 * The design's `timeline4` promise card. Row 2 ("joins N others") only renders once someone other
 * than the viewer has reached out.
 */
const PromiseCard: React.FC<PromiseCardProps> = ({ decisionMakerName, otherSupporterCount }) => {
	const { localizeNumber } = useI18n();

	return (
		<ol
			className="border-stroke-weaker relative mt-6 flex w-full list-none flex-col gap-4 rounded-md border border-solid p-4 text-left"
			data-testid="contact-decision-maker-success-promises"
		>
			{/* The rail runs behind the markers, from the first marker's centre to the last's. */}
			<span aria-hidden className="bg-stroke-weaker absolute top-7 bottom-7 left-7 -ml-px w-0.5" />
			<PromiseRow tone="done" icon={<CheckIcon size="x-small" className="text-icon-inverse-strong" />}>
				<Translate value="corgi.contact_decision_maker.success.promise_received" />
			</PromiseRow>
			{!!otherSupporterCount && (
				// Intl output must not differ between SSR and hydration, same as the sub-line above.
				<ClientRender>
					<PromiseRow tone="next" icon={<GroupIcon size="x-small" className="text-icon-information" />}>
						<TranslatePlural
							value="corgi.contact_decision_maker.success.promise_joins"
							count={otherSupporterCount}
							replacements={{ otherCount: localizeNumber(otherSupporterCount) }}
						/>
					</PromiseRow>
				</ClientRender>
			)}
			<PromiseRow tone="next" icon={<SendIcon size="x-small" className="text-icon-information" />}>
				<Translate
					value="corgi.contact_decision_maker.success.promise_delivery"
					replacements={{ decision_maker_name: decisionMakerName }}
				/>
			</PromiseRow>
			<PromiseRow tone="next" icon={<EmailIcon size="x-small" className="text-icon-information" />}>
				<Translate
					value="corgi.contact_decision_maker.success.promise_reply"
					replacements={{ decision_maker_name: decisionMakerName }}
				/>
			</PromiseRow>
		</ol>
	);
};

type PromiseCardProps = {
	decisionMakerName: string;
	/** Everyone but the viewer; `undefined` while the count is loading. The row is hidden at 0. */
	otherSupporterCount: number | undefined;
};

/**
 * One step on the promise timeline. The marker is a 24px circle; the `done` tone is the design's
 * filled green "received" marker, `next` the tinted markers for what Change.org does from here.
 */
const PromiseRow: React.FC<PromiseRowProps> = ({ tone, icon, children }) => (
	<li className="flex items-center gap-3">
		{/* The ring masks the rail where it passes behind the marker. */}
		<span
			aria-hidden
			className={cn(
				'ring-background-base relative flex size-6 shrink-0 items-center justify-center rounded-full ring-4',
				tone === 'done' ? 'bg-fill-success-strong' : 'bg-fill-information-weaker',
			)}
		>
			{icon}
		</span>
		<Text as="span" className="text-size-body-small text-typography-strong">
			{children}
		</Text>
	</li>
);

type PromiseRowProps = {
	tone: 'done' | 'next';
	icon: React.ReactNode;
	children: React.ReactNode;
};
